import { useContext, useState, useEffect } from "react";
import StarekContext from "../../context/StarekContext";
import { gql, useQuery, useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import Select from "react-select";
import HierarchicalDeviceSelector from "../../components/HierarchicalDeviceSelector";

export default function ReportFilter({
    filters: initialFilters,
    onChange, // We won't use this directly anymore for immediate updates
    onReset,
    onApply,
    onClose,
}) {
    // Local state for filters
    const [localFilters, setLocalFilters] = useState(initialFilters);

    // Sync local state if initialFilters change (e.g. when default shift is loaded)
    useEffect(() => {
        setLocalFilters(initialFilters);
    }, [initialFilters]);

    const handleLocalChange = (field, value) => {
        setLocalFilters((prev) => ({ ...prev, [field]: value }));
    };

    const starekContext = useContext(StarekContext);

    const GET_OPERATIONS_IN_PLANT = gql`
        query GetOperationsForReports($where: OperationWhereInput!) {
            operations(where: $where) {
                id
                name
                is_productive
            }
        }
    `;

    const GET_DEVICES_IN_PLANT = gql`
        query GetDevicesForPlant($where: ShadowDeviceWhereInput) {
            shadowDevices(where: $where) {
                id
                name
                device
                parameters
                jobSchedules {
                    id
                    name
                    start_time
                    end_time
                }
            }
        }
    `;

    const GET_SHIFT_LIST_FOR_PLANT = gql`
        query GetShifts($plantId: Int!) {
            shifts(where: { plant_id: { equals: $plantId } }) {
                id
                name
                start_time
                end_time
            }
        }
    `;

    const {
        data: deviceData,
        loading: deviceLoading,
        error: deviceError,
        refetch: refetchDevices,
    } = useQuery(GET_DEVICES_IN_PLANT, {
        variables: {
            where: {
                plant: {
                    is: {
                        id: {
                            equals: starekContext.lastUsedPlant?.id,
                        },
                    },
                },
            },
        },
        pollInterval: 60000,
    });

    const {
        data: shiftData,
        loading: shift,
        error: shiftError,
        refetch: refetchShifts,
    } = useQuery(GET_SHIFT_LIST_FOR_PLANT, {
        variables: { plantId: starekContext.lastUsedPlant.id },
    });
    const nowLocal = new Date().toISOString().slice(0, 16);
    const today = new Date().toISOString().slice(0, 10);
    const intervalOptions = [
        { label: "1 hour", value: "1h", seconds: 3600 },
        { label: "2 hours", value: "2h", seconds: 7200 },
        { label: "4 hours", value: "4h", seconds: 14400 },
        { label: "6 hours", value: "6h", seconds: 21600 },
        { label: "12 hours", value: "12h", seconds: 43200 },
        { label: "18 hours", value: "18h", seconds: 64800 },
        { label: "1 day", value: "1d", seconds: 86400 },
        { label: "1 week", value: "7d", seconds: 604800 },
        { label: "1 month", value: "30d", seconds: 2592000 },
    ];
    const { data: operationData, loading: operationLoading } = useQuery(
        GET_OPERATIONS_IN_PLANT,
        {
            client: starekContext.engineClient,
            skip: !starekContext.lastUsedPlant?.id,
            fetchPolicy: "network-only",
            variables: {
                where: {
                    plant_id: { equals: starekContext.lastUsedPlant?.id },
                    is_active: { equals: true },
                },
            },
        },
    );

    const operationOptions = (operationData?.operations ?? []).map((op) => ({
        value: String(op.id),
        label: op.name,
    }));

    const selectedOperationOptions = operationOptions.filter((opt) =>
        (localFilters.operations || "").split(",").includes(opt.value),
    );

    const shadowDevices = deviceData?.shadowDevices ?? [];

    // Get selected device IDs from comma-separated string
    const selectedDeviceIds = localFilters.devices
        ? localFilters.devices.split(",").filter((id) => id)
        : [];

    const fetchDevices = async () => {
        try {
            await refetchDevices();
        } catch (error) {
            console.error("Error refetching devices");
        }
    };
    const getDurationSeconds = () => {
        const d = localFilters.duration;

        if (!d) return 0;

        // SHIFT SELECTED
        if (d.startsWith("shift_")) {
            const shiftId = parseInt(d.replace("shift_", ""));
            const shiftObj = shiftData?.shifts?.find((s) => s.id === shiftId);
            if (!shiftObj) return 0;

            const start = Number(shiftObj.start_time); // seconds
            const end = Number(shiftObj.end_time); // seconds

            let diff = end - start;
            if (diff < 0) diff += 24 * 3600; // cross midnight shift support

            return diff;
        }

        // BY DURATION OPTIONS ("8h", "7d", "30d")
        if (d.endsWith("h")) return Number(d.replace("h", "")) * 3600;

        if (d.endsWith("d")) return Number(d.replace("d", "")) * 86400;

        // CUSTOM RANGE
        if (d === "custom") {
            if (!localFilters.customStart || !localFilters.customEnd) return 0;
            const s = new Date(localFilters.customStart).getTime();
            const e = new Date(localFilters.customEnd).getTime();
            if (e <= s) return 0;
            return (e - s) / 1000;
        }

        return 0;
    };
    const mapIntervalToSeconds = (val) => {
        if (!val) return 0;
        if (val.endsWith("h")) {
            return Number(val.replace("h", "")) * 3600;
        }
        if (val.endsWith("d")) {
            return Number(val.replace("d", "")) * 86400;
        }

        return 0;
    };

    const buildFilterPayload = () => {
        const d = localFilters.duration;

        let start = null;
        let end = null;

        if (d.startsWith("shift_")) {
            const shiftId = parseInt(d.replace("shift_", ""));
            const shift = shiftData?.shifts?.find((s) => s.id === shiftId);
            if (shift && localFilters.shiftDate) {
                const base = new Date(localFilters.shiftDate);
                const startDate = new Date(base);
                const endDate = new Date(base);

                startDate.setSeconds(shift.start_time);
                endDate.setSeconds(shift.end_time);
                if (shift.end_time < shift.start_time) {
                    endDate.setDate(endDate.getDate() + 1);
                }

                start = startDate.toISOString();
                end = endDate.toISOString();
            }
        } else if (d.endsWith("h") || d.endsWith("d")) {
            const now = new Date();
            const durationSeconds = getDurationSeconds();
            start = new Date(
                now.getTime() - durationSeconds * 1000,
            ).toISOString();
            end = now.toISOString();
        } else if (d === "custom") {
            if (localFilters.customStart && localFilters.customEnd) {
                start = new Date(localFilters.customStart).toISOString();
                end = new Date(localFilters.customEnd).toISOString();
            }
        }

        return {
            ...localFilters,
            durationStart: start,
            durationEnd: end,
            durationSeconds: getDurationSeconds(),
            intervalSeconds: mapIntervalToSeconds(localFilters.interval),
        };
    };

    return (
        <>
            <div>
                <div className="tw-flex tw-items-center tw-justify-between">
                    <h3 className="tw-text-base tw-font-semibold tw-text-slate-900">
                        Filters
                    </h3>

                    <button
                        type="button"
                        onClick={onClose}
                        className="tw-inline-flex tw-items-center tw-justify-center tw-h-7 tw-w-7 tw-rounded-full tw-border tw-border-slate-300 tw-text-slate-600 hover:tw-bg-slate-100 hover:tw-text-slate-800"
                        title="Close filters"
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </button>
                </div>

                {/* Devices - Hierarchical Selector */}
                <div className="tw-mt-4">
                    <label className="tw-block tw-text-xs tw-font-medium tw-text-slate-700 tw-mb-2">
                        Devices &amp; Installations
                    </label>

                    <HierarchicalDeviceSelector
                        selectedDevices={selectedDeviceIds}
                        onSelectionChange={(deviceIds) => {
                            handleLocalChange("devices", deviceIds.join(","));
                        }}
                        plantId={starekContext.lastUsedPlant?.id}
                        loading={deviceLoading}
                    />
                </div>

                {/* Operations — filter the report to specific work */}
                <div className="tw-mt-4">
                    <label className="tw-block tw-text-xs tw-font-medium tw-text-slate-700 tw-mb-2">
                        Operations
                    </label>
                    <Select
                        options={operationOptions}
                        isMulti
                        isLoading={operationLoading}
                        placeholder="All operations"
                        value={selectedOperationOptions}
                        onChange={(vals) =>
                            handleLocalChange(
                                "operations",
                                (vals || []).map((v) => v.value).join(","),
                            )
                        }
                        styles={{
                            control: (base) => ({ ...base, fontSize: "0.75rem" }),
                            menu: (base) => ({ ...base, fontSize: "0.75rem" }),
                        }}
                    />
                    <p className="tw-mt-1 tw-text-[0.68rem] tw-text-slate-500">
                        Leave empty to include every operation.
                    </p>
                </div>

                {/* Duration */}
                {localFilters.devices && (
                    <div className="tw-mt-4">
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-slate-700">
                            Duration
                        </label>

                        <select
                            className="tw-mt-1 tw-w-full tw-border tw-border-slate-300 tw-px-2 tw-py-1.5 tw-text-xs tw-text-black focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 focus:tw-border-blue-500"
                            value={localFilters.duration}
                            onChange={(e) =>
                                handleLocalChange("duration", e.target.value)
                            }
                        >
                            <option value="">Select</option>
                            <optgroup label="By Shift">
                                {shiftData?.shifts?.map((shift) => (
                                    <option
                                        key={shift.id}
                                        value={`shift_${shift.id}`}
                                    >
                                        {shift.name}
                                    </option>
                                ))}
                            </optgroup>
                            <optgroup label="By Duration">
                                <option value="1h">Last 1 Hour</option>
                                <option value="4h">Last 4 Hours</option>
                                <option value="8h">Last 8 Hours</option>
                                <option value="10h">Last 10 Hours</option>
                                <option value="12h">Last 12 Hours</option>
                                <option value="24h">Last 24 Hours</option>
                                <option value="7d">Last 1 Week</option>
                                <option value="30d">Last 1 Month</option>
                            </optgroup>
                            <optgroup label="Custom">
                                <option value="custom">Choose Time</option>
                            </optgroup>
                        </select>

                        {/* SHIFT SELECTED → show 1 DATE field */}
                        {localFilters.duration.startsWith("shift_") && (
                            <div className="tw-mt-3">
                                <label className="tw-text-xs tw-text-slate-700">
                                    Select Date
                                </label>
                                <input
                                    type="date"
                                    className="tw-mt-1 tw-w-full tw-border tw-border-slate-300 tw-text-xs tw-px-2 tw-py-1.5"
                                    max={today}
                                    value={localFilters.shiftDate || ""}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value > today) return;
                                        handleLocalChange("shiftDate", value);
                                    }}
                                />
                            </div>
                        )}

                        {/* CUSTOM SELECTED → show 2 DATETIME fields */}
                        {localFilters.duration === "custom" && (
                            <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-3">
                                {/* Start Time */}
                                <div>
                                    <label className="tw-text-xs tw-text-slate-700">
                                        Start Time
                                    </label>
                                    <input
                                        type="datetime-local"
                                        className="tw-mt-1 tw-w-full tw-border tw-border-slate-300 tw-text-xs tw-px-2 tw-py-1.5"
                                        max={nowLocal}
                                        value={localFilters.customStart || ""}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value > nowLocal) return;
                                            if (
                                                localFilters.customEnd &&
                                                localFilters.customEnd < value
                                            ) {
                                                handleLocalChange(
                                                    "customEnd",
                                                    value,
                                                );
                                            }

                                            handleLocalChange(
                                                "customStart",
                                                value,
                                            );
                                        }}
                                    />
                                </div>

                                {/* End Time */}
                                <div>
                                    <label className="tw-text-xs tw-text-slate-700">
                                        End Time
                                    </label>
                                    <input
                                        type="datetime-local"
                                        className="tw-mt-1 tw-w-full tw-border tw-border-slate-300 tw-text-xs tw-px-2 tw-py-1.5"
                                        max={nowLocal}
                                        min={localFilters.customStart || ""}
                                        value={localFilters.customEnd || ""}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value > nowLocal) return;
                                            if (
                                                localFilters.customStart &&
                                                value < localFilters.customStart
                                            ) {
                                                return;
                                            }

                                            handleLocalChange(
                                                "customEnd",
                                                value,
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Interval */}
                {localFilters.duration && (
                    <div className="tw-mt-4">
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-slate-700">
                            Interval
                        </label>

                        <select
                            className="tw-mt-1 tw-w-full tw-border tw-border-slate-300 tw-px-2 tw-py-1.5 tw-text-xs tw-text-slate-800 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 focus:tw-border-blue-500"
                            value={localFilters.interval}
                            onChange={(e) =>
                                handleLocalChange("interval", e.target.value)
                            }
                        >
                            <option value="">Select</option>

                            {intervalOptions.map((opt) => {
                                const durationSeconds = getDurationSeconds();
                                const isDisabled =
                                    opt.seconds > durationSeconds;

                                return (
                                    <option
                                        key={opt.value}
                                        value={opt.value}
                                        disabled={isDisabled}
                                        className={
                                            isDisabled
                                                ? "tw-text-slate-400"
                                                : "tw-text-black"
                                        }
                                    >
                                        {opt.label}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                )}
            </div>

            {/* Footer buttons */}
            <div className="tw-mt-auto tw-pt-4 tw-flex tw-gap-3">
                <button
                    type="button"
                    onClick={() => {
                        onReset();
                        setLocalFilters({
                            devices: "",
                            interval: "",
                            duration: "",
                            shiftDate: "",
                        });
                    }}
                    className="btn btn-outline-primary tw-flex-1"
                >
                    Reset
                </button>
                <button
                    type="button"
                    onClick={() => {
                        const payload = buildFilterPayload();
                        onApply(payload);
                        onClose();
                    }}
                    className="btn btn-primary tw-flex-1"
                >
                    Apply
                </button>
            </div>
        </>
    );
}
